import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';

import { forkJoin, Observable, of } from 'rxjs';

import { LookupService } from '../lookup.service';
import { SubjectTypeService } from '../subject-type.service';
import { CategoryService } from '../category.service';
import { StatusService } from '../status.service';
import { UserService } from '../user.service';
import { plural } from 'pluralize';
import { ProcessTypeService } from '../process-type.service';
import { map, switchMap } from 'rxjs/operators';
import { LookupTypeService } from '../lookup-type.service';
import {
  enumGroupReportReturnType,
  Report,
  ReportConfig,
  ReportData,
  ReportOptions,
} from '../../models/report';
import { DataField } from '../../models/data-field';
import { ProcessType } from '../../models/process-type';
import { OrganisationService } from '../organisation.service';
import { TaskTypeService } from '../task-type.service';
import { SubjectType } from '../../models/subject-type';
import { User } from '../../models/user';
import { BaseReportService, IReportService } from './base-report.service';
import { EventService } from '../event.service';
import { environment } from 'environments/environment';
import { Guid } from 'app/shared/helpers/guid';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class SystemReportService
  extends BaseReportService
  implements IReportService, OnDestroy
{
  subjectType: SubjectType; // keep track of the current subject type
  currentOptions: ReportOptions; // keep track of currently applied options

  constructor(
    http: HttpClient,
    private subjectTypeService: SubjectTypeService,
    private processTypeService: ProcessTypeService,
    private categoryService: CategoryService,
    private lookupService: LookupService,
    private taskTypeService: TaskTypeService,
    private statusService: StatusService,
    private organisationService: OrganisationService,
    private eventService: EventService,
    protected userService: UserService,
    protected lookupTypeService: LookupTypeService,
    protected router: Router
  ) {
    super(
      SystemReportService,
      http,
      'api/reports',
      'systemreports',
      router,
      lookupTypeService,
      userService
    );

    this.cacheEnabled = true;
  }

  ngOnDestroy(): void {}

  itemChanged(val: any) {
    this.onItemChanged.next(val);
    this.eventService.reportItemChanged(val);
  }

  getAllData(
    type?: string,
    urlParams?: string,
    forceRefresh?: boolean
  ): Observable<Report[]> {
    return super.getAllData(type, urlParams, forceRefresh).pipe(
      switchMap((reports) => {
        // add tasks by task type report
        const report = this.createReportsForTasks();
        reports.push(report);

        // add auto generated subject / process reports
        return this.getAutoGeneratedReportsBySubjectTypeId(1).pipe(
          map((autoGeneratedReports) => {
            // update cache and return cache contents
            reports.push(...autoGeneratedReports);
            return this.updateCache(reports);
          })
        );
      })
    );
  }

  getReportData(report: Report, options: ReportOptions): void {
    let url = this.buildUrl(report, options);
    const taskName = `${options.filters || 'X'}`;
    const taskState = `${options.subFilters || 'X'}`;
    const taskActionFor = `${options.users || 'X'}`;
    const taskDateFilter = `${options.dateType || 'X'}/${
      options.dateFrom || 'X'
    }/${options.dateTo || 'X'}`;
    const taskReportTypeFilter = `${options.reportType || 'X'}`;
    const taskOrganisation = `${options.organisations || 'X'}`;
    const taskIncludeArchived = `${options.includeArchived}`;
    //need to also have it here so we can pass it all back
    //const includeRecordsNotLinkedToSubject = `${report.config.includeRecordsNotLinkedToSubject}`;

    // link to subject, link that to subject organisations, then compare ids. this is for organisation id. Add table organisations. also change to restricted subjects instead of subject in sql.
    const fetch$ = [];
    fetch$.push(of(null));

    if (report.config.filterDataType === 'tasktypes') {
      url =
        this.resourceUrl +
        `/taskReport/${taskName}/${taskState}/${taskActionFor}/${taskReportTypeFilter}/${taskDateFilter}/${taskOrganisation}/${taskIncludeArchived}`;

      //add to the resource url
      forkJoin(fetch$).subscribe((_) => {
        this.http
          .get<ReportData>(url, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              timeout: '120000',
            }),
          })
          .subscribe((data: ReportData) => {
            this.eventService.reportDataChanged({
              reportData: data,
            });
          });
      });
    }

    const processTypeId = report.config.processTypeId || null;
    const subjectTypeId = report.config.subjectTypeId || null;

    //includeRecordsNotLinkedToSubject

    //alter backend
    forkJoin(fetch$).subscribe((_) => {
      this.http.get<ReportData>(url).subscribe((data: ReportData) => {
        this.eventService.reportDataChanged({
          subjectTypeId,
          processTypeId,
          reportData: data,
        });
      });
    });
  }

  getAutoGeneratedReportsBySubjectTypeId(
    subjectTypeId: number
  ): Observable<Report[]> {
    return this.subjectTypeService.getById(subjectTypeId).pipe(
      map((st) => {
        this.subjectType = st;
        return st ? this.getAutoGeneratedReportsBySubjectType(st) : [];
      })
    );
  }

  getAutoGeneratedReportsBySubjectType(subjectType: SubjectType): Report[] {
    const reports: Report[] = [];
    const INCLUDEFIELDS = ['categoryid', 'status'];
    subjectType.dataFields.forEach((item) => {
      if (
        INCLUDEFIELDS.indexOf(item.name.toLowerCase()) > -1 ||
        item.dataType.toLowerCase() === 'user' ||
        /^\d+$/.test(item.dataType)
      ) {
        const displayName = `${plural(subjectType.name)} by ${item.label}`;
        const description = `Count of ${plural(subjectType.name)} by ${
          item.label
        }`;
        reports.push(
          this.createReport(
            displayName,
            description,
            item,
            undefined,
            item,
            undefined,
            undefined,
            undefined,
            subjectType.id
          )
        );
      }
    });

    // we also want to be able to report on count of due/late tasks ...
    const dataField = subjectType.dataFields.find(
      (i) => i.name.toLowerCase() === 'userid'
    );
    if (dataField) {
      const displayName = `${plural(subjectType.name)} by ${
        dataField.label
      } with Due/Late Tasks`;
      const description = `Count of ${plural(subjectType.name)} by ${
        dataField.label
      } with Due/Late Tasks`;
      reports.push(
        this.createReport(
          displayName,
          description,
          dataField,
          undefined,
          dataField,
          undefined,
          undefined,
          { id: 'NumTasks', label: 'Due/Late Tasks', type: 'count' },
          subjectType.id
        )
      );
    }

    // outcomes are a special case, so handle separately
    const processType = this.processTypeService
      .getCache()
      .find((i) => i.name.toLowerCase() === 'outcome');
    if (processType) {
      // get the datafield corresponding to the outcome coding - all outcome reports should have sub filter for outcome coding
      const outcomeCodingDataField = processType.dataFields.find(
        (i) => i.name.toLowerCase() === 'outcomecoding'
      );

      // get the generic reports for the outcome process type
      reports.push(
        ...this.getAutoGeneratedReportsByProcessType(
          processType,
          undefined,
          outcomeCodingDataField
        )
      );

      // add reports grouped by subject field
      reports.push(
        ...this.createReportsForProcessTypeGroupedBySubjectField(
          processType,
          subjectType,
          ['campaign', 'userid', 'type', 'informationsource'],
          outcomeCodingDataField
        )
      );
    }

    const categoryDataField = subjectType.dataFields.find(
      (i) => i.name.toLowerCase() === 'categoryid'
    );

    if (categoryDataField) {
      // handle any other process types associated with the subject
      // TODO:  Check with PD whether we can iterate through all available process types...
      reports.push(
        ...this.getAutoGeneratedReportsByProcessTypeName(
          'time record',
          categoryDataField
        )
      );
    }
    // if (casetypeDataField) {
    //   reports.push(
    //     ...this.getAutoGeneratedReportsByProcessTypeName(
    //       'time record',
    //       casetypeDataField
    //     )
    //   );
    // }

    reports.push(...this.timeRecordByCaseTypeReport(subjectType));

    return reports;
  }

  getAutoGeneratedReportsByProcessTypeName(
    processTypeName: string,
    filter?: DataField
  ): Report[] {
    const processType = this.processTypeService
      .getCache()
      .find((i) => i.name.toLowerCase() === processTypeName);
    return processType
      ? this.getAutoGeneratedReportsByProcessType(processType, filter)
      : [];
  }

  getAutoGeneratedReportsByProcessType(
    processType: ProcessType,
    filter?: DataField,
    subFilter?: DataField
  ): Report[] {
    const reports: Report[] = [];
    if (processType) {
      let userFilter;
      const userDataFields = processType.dataFields.filter(
        (i) => i.dataType.toLowerCase() === 'user'
      );
      if (userDataFields.length > 0) {
        userFilter = userDataFields[0];
      }
      const valueDataFields = processType.dataFields.filter(
        (i) =>
          i.dataType.toLowerCase() === 'currency' ||
          i.dataType.toLowerCase() === 'number'
      ); // number data fields which we could sum
      if (valueDataFields.length > 0) {
        valueDataFields.forEach((i) => {
          const r = this.createReportsForProcessType(
            processType,
            filter,
            subFilter,
            userFilter,
            i
          );
          if (r.length > 0) {
            reports.push(...r);
          }
        });
      } else {
        const r = this.createReportsForProcessType(
          processType,
          filter,
          subFilter,
          userFilter
        );
        if (r.length > 0) {
          reports.push(...r);
        }
      }
    }
    return reports;
  }

  createReportsForProcessType(
    processType: ProcessType,
    filter?: DataField,
    subFilter?: DataField,
    userFilter?: DataField,
    valueDataField?: DataField
  ): Report[] {
    const reports: Report[] = [];
    if (processType) {
      processType.dataFields.forEach((item) => {
        if (
          item.dataType.toLowerCase() === 'user' ||
          /^\d+$/.test(item.dataType)
        ) {
          const displayName = `${plural(valueDataField.label)} by ${
            item.label
          }`;

          const description = `Sum of ${processType.name} ${plural(
            valueDataField.label
          )} by ${item.label}`;
          const f = filter || item;
          let sub = filter ? subFilter || item : subFilter;
          if (sub && sub.id === f.id) {
            sub = undefined;
          }
          let userField;
          if (
            item.dataType.toLowerCase() !== 'user' &&
            (!userFilter ||
              !sub ||
              (sub.id !== userFilter.id && f.id !== userFilter.id))
          ) {
            userField = userFilter;
          }
          const report = valueDataField
            ? this.createReport(
                displayName,
                `Sum of ${processType.name} ${plural(
                  valueDataField.label
                )} by ${item.label}`,
                item,
                undefined,
                f,
                sub,
                userField,
                {
                  id: valueDataField.id,
                  label: valueDataField.label,
                  type: 'sum',
                },
                undefined,
                processType.id,
                processType.name.toLowerCase() === 'time record' ? true : false
              )
            : this.createReport(
                displayName,
                `Count of ${processType.name} ${plural(item.label)}`,
                item,
                undefined,
                f,
                sub,
                userField,
                undefined,
                undefined,
                processType.id,
                processType.name.toLowerCase() === 'time record' ? true : false
              );
          if (report) {
            // the default return type is to include process, not subject
            report.config.returnType = enumGroupReportReturnType.Process;
            // add report to list of reports to return
            reports.push(report);
          }
        }
      });
    }
    return reports;
  }

  timeRecordByCaseTypeReport(subjectType: SubjectType): Report[] {
    const reports = [];
    const processType = this.processTypeService
      .getCache()
      .find((i) => i.name.toLowerCase() === 'time record');

    const valueDataFields = processType.dataFields.filter(
      (i) => i.dataType.toLowerCase() === 'number'
    );
    const casetype = subjectType.dataFields.find(
      (i) =>
        i.label.toLowerCase() === 'type' ||
        i.label.toLowerCase() === 'case type'
    );
    const r = this.createReport(
      'Time Values by Case Type',
      'Sum of Time Record Time Values by Case Type',
      casetype,
      undefined,
      casetype,
      undefined,
      processType.dataFields.find((i) => i.name.toLowerCase() === 'user'),
      {
        id: valueDataFields[0].id,
        label: valueDataFields[0].label,
        type: 'sum',
      },
      undefined,
      processType.id,
      false
    );
    if (r) {
      r.config.returnType = enumGroupReportReturnType.SubjectAndProcess;
    }
    reports.push(r);
    return reports;
  }

  createReportsForProcessTypeGroupedBySubjectField(
    processType: ProcessType,
    subjectType: SubjectType,
    fieldNames: string[],
    subFilter?: DataField
  ): Report[] {
    const reports: Report[] = [];
    if (processType && subjectType && fieldNames.length > 0) {
      fieldNames.forEach((f) => {
        const dataField = subjectType.dataFields.find(
          (i) => i.name.toLowerCase() === f.toLowerCase()
        );

        // if (dataField.name == 'type') {
        //   dataField.label = 'Case Type';
        // }

        if (dataField) {
          let userFilter;
          const userDataFields = processType.dataFields.filter(
            (i) => i.dataType.toLowerCase() === 'user'
          );
          if (userDataFields.length > 0) {
            userFilter = userDataFields[0];
          }
          const valueDataFields = processType.dataFields.filter(
            (i) =>
              i.dataType.toLowerCase() === 'currency' ||
              i.dataType.toLowerCase() === 'number'
          ); // number data fields which we could sum
          if (valueDataFields.length > 0) {
            valueDataFields.forEach((i) => {
              let displayName = `${plural(i.label)} by ${dataField.label}`;
              let descriptionType = '';

              if (dataField.label === 'Type') {
                displayName = `${plural(i.label)} by Case Type`;
                descriptionType = `Sum of ${processType.name} ${plural(
                  i.label
                )} by Case Type`;
              }

              reports.push(
                this.createReport(
                  displayName,
                  descriptionType !== ''
                    ? descriptionType
                    : `Sum of ${processType.name} ${plural(i.label)} by ${
                        dataField.label
                      }`,
                  dataField,
                  undefined,
                  dataField,
                  subFilter,
                  userFilter,
                  { id: i.id, label: i.label, type: 'sum' },
                  undefined,
                  processType.id
                )
              );
            });
          }
        }
      });
    }
    return reports;
  }

  createReportsForTasks(
    taskName?: string,
    state?: string,
    users?: string,
    dateFilter?: string,
    dateFrom?: string,
    dateTo?: string
  ) {
    const reportConfig = new ReportConfig({});
    reportConfig.filterId = 1;
    reportConfig.filterDataType = 'tasktypes';
    reportConfig.filterLabel = 'Task Types';
    reportConfig.subFilterId = 1;
    reportConfig.subFilterDataType = 'taskstatus';
    reportConfig.subFilterLabel = 'Task Statuses';
    reportConfig.userFilterLabel = 'Action For';

    const report = new Report({
      uid: Guid.newGuid(),
      name: 'Tasks by Task Type',
      description: 'Count of Tasks by Task Type',
      reportType: 'Task Report',
      config: reportConfig,
      canDelete: false,
    });

    return report;
  }

  getFilters(dataType: string): Observable<any> {
    let source$: Observable<any[]>;
    switch (dataType.toLowerCase()) {
      case 'category':
        source$ = this.categoryService.getCategoriesBySubjectType(
          this.subjectType.id || 1
        );
        break;

      case 'status':
        source$ = this.statusService.getAllData('Status').pipe(
          map((data) => {
            if (environment.enableFOIFeature === false) {
              data = data.filter((i) => {
                return i.state.uid != 'c8318c86-f0e2-4c09-86eb-04d97270aaaa';
              });
            }
            return data;
          })
        );
        break;

      case 'user':
        // MRD - replaced getActiveUsers() with getAllData() so all available users are returned as agreed with PD, 11/03/2020
        source$ = this.userService.getAllData();
        break;

      case 'organisation':
        source$ = this.organisationService.getAllData();
        break;

      case 'tasktypes':
        source$ = this.taskTypeService.getAllData();
        break;

      case 'taskstatus':
        const taskStateArray = [
          { id: 10, description: 'Open' },
          { id: 20, description: 'Late' },
          { id: 30, description: 'Completed' },
          { id: 40, description: 'Abandoned' },
          { id: 50, description: 'Due' },
        ];
        //if id is 10 and date is today then due

        source$ = of(taskStateArray);
        break;

      default:
        if (/^\d+$/.test(dataType)) {
          source$ = this.lookupService.getAllByLookupType(Number(dataType));
        } else {
          source$ = this.lookupService.getAllByLookupTypeDescription(dataType);
        }
        break;
    }

    if (source$) {
      return source$.pipe(
        map((data) => {
          const filterList = [];
          if (data.length > 0 && dataType.toLowerCase() === 'user') {
            data = data.map((user) => new User(user));
            this.currentUser = this.userService.getCurrentUser();
            if (environment.limitAccessToSubjectsByOrg === true) {
              if (this.currentUser && this.currentUser.organisationId) {
                data = data.filter(
                  (i) => i.organisationId === this.currentUser.organisationId
                );
              }
            }
          }
          data.forEach((item) => {
            filterList.push({
              filterValue: item.id,
              filterName: item.description || item.name || item.fullName,
            });
          });
          // tslint:disable-next-line:only-arrow-functions
          return filterList.sort(function (a, b) {
            return a.filterName.localeCompare(b.filterName);
          });
        })
      );
    }
  }
}
